import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Trash2, Edit2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export default function AdminRedirects() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [saving, setSaving] = useState(false);

    const load = () => { setLoading(true); api.get('/admin/redirects').then(({ data }) => setItems(data)).finally(() => setLoading(false)); };
    useEffect(load, []);

    const open = (item) => { setEditing(item ? { ...item } : { old_url: '/', new_url: '/', type: 301, active: true }); setDialogOpen(true); };
    const close = () => { setDialogOpen(false); setEditing(null); };

    const onSave = async () => {
        setSaving(true);
        try {
            if (editing.id) {
                await api.put(`/admin/redirects/${editing.id}`, editing);
                toast.success('Redirect ažuriran');
            } else {
                await api.post('/admin/redirects', editing);
                toast.success('Redirect kreiran');
            }
            close(); load();
        } catch (e) { toast.error(e?.response?.data?.detail || 'Spremanje nije uspjelo'); } finally { setSaving(false); }
    };

    const onDelete = async (id) => {
        if (!confirm('Obrisati redirect?')) return;
        try { await api.delete(`/admin/redirects/${id}`); toast.success('Obrisano'); load(); } catch { toast.error('Brisanje nije uspjelo'); }
    };

    return (
        <div>
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-medium">Redirecti</h1>
                    <p className="text-sm text-white/60 mt-1">301/302 preusmjerenja sa starih na nove URL-ove.</p>
                </div>
                <Button onClick={() => open(null)} className="bg-[#B08D57] hover:bg-[#9c7d4e] text-white"><Plus className="mr-2 h-4 w-4" />Novi redirect</Button>
            </div>

            <Card className="mt-6 bg-[#111318] border-[#232A36] overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="text-white/60 text-xs uppercase tracking-wider">
                        <tr className="border-b border-[#232A36]">
                            <th className="text-left px-4 py-3">Stari URL</th>
                            <th className="text-left px-4 py-3">Novi URL</th>
                            <th className="text-left px-4 py-3">Tip</th>
                            <th className="text-left px-4 py-3">Aktivno</th>
                            <th className="text-right px-4 py-3">Akcije</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? <tr><td colSpan="5" className="p-8 text-center text-white/60">Učitavanje…</td></tr> :
                          items.length === 0 ? <tr><td colSpan="5" className="p-8 text-center text-white/60">Nema redirecta.</td></tr> :
                          items.map((r) => (
                            <tr key={r.id} className="border-b border-[#232A36] hover:bg-[#161A21]">
                                <td className="px-4 py-3 font-mono text-xs">{r.old_url}</td>
                                <td className="px-4 py-3 font-mono text-xs">{r.new_url}</td>
                                <td className="px-4 py-3">{r.type}</td>
                                <td className="px-4 py-3">{r.active ? '✓' : '✘'}</td>
                                <td className="px-4 py-3 text-right">
                                    <Button size="sm" variant="outline" onClick={() => open(r)} className="border-[#232A36] text-white hover:bg-[#232A36] mr-2"><Edit2 className="h-3.5 w-3.5" /></Button>
                                    <Button size="sm" variant="outline" onClick={() => onDelete(r.id)} className="border-red-900 text-red-400 hover:bg-red-900/20"><Trash2 className="h-3.5 w-3.5" /></Button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </Card>

            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="max-w-lg bg-[#111318] border-[#232A36] text-white">
                    <DialogHeader><DialogTitle>{editing?.id ? 'Uredi redirect' : 'Novi redirect'}</DialogTitle></DialogHeader>
                    {editing && (
                        <div className="space-y-4">
                            <div><Label>Stari URL (npr. /stara-stranica)</Label><Input value={editing.old_url} onChange={(e) => setEditing({...editing, old_url: e.target.value})} className="mt-2 bg-[#0B0C0E] border-[#232A36] font-mono" /></div>
                            <div><Label>Novi URL (relativni ili apsolutni)</Label><Input value={editing.new_url} onChange={(e) => setEditing({...editing, new_url: e.target.value})} className="mt-2 bg-[#0B0C0E] border-[#232A36] font-mono" /></div>
                            <div>
                                <Label>Tip</Label>
                                <Select value={String(editing.type)} onValueChange={(v) => setEditing({...editing, type: parseInt(v)})}>
                                    <SelectTrigger className="mt-2 bg-[#0B0C0E] border-[#232A36]"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="301">301 – Permanent</SelectItem>
                                        <SelectItem value="302">302 – Temporary</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="flex items-center gap-3">
                                <Switch checked={editing.active} onCheckedChange={(v) => setEditing({...editing, active: v})} />
                                <Label>Aktivno</Label>
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={close} className="border-[#232A36] text-white hover:bg-[#232A36]">Odustani</Button>
                        <Button onClick={onSave} disabled={saving} className="bg-[#B08D57] hover:bg-[#9c7d4e] text-white">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Spremi'}</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
